import { formatUserName } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { TableToolbar, TextTool } from "@impero-com/ui";
import { useOutletContext } from "react-router-dom";

import { AssignmentTable } from "org/user/components/AssignmentTable";
import { RemoveUserModal } from "org/user/components/RemoveUserModal";
import { ReplaceUserModal } from "org/user/components/ReplaceUserModal";
import { useControlProgramsForUserQuery } from "org/user/queries/useControlProgramsForUserQuery";
import { OutletContext } from "org/user/UserViewPage";
import { getFilterModeConfig } from "utils/table/getFilterModeConfig";

import { useRemoveControlProgramAccessRightUtils } from "./useRemoveControlProgramAccessRightUtils";
import { useReplaceControlProgramUserUtils } from "./useReplaceControlProgramUserUtils";
import {
    ControlProgramRow,
    useUserControlProgramListTable,
} from "./useUserControlProgramListTable";

export function UserControlProgramsList() {
    const { user } = useOutletContext<OutletContext>();
    const userId = user.id;
    const { data: controlPrograms } = useControlProgramsForUserQuery(userId);
    const { t } = useTranslation();

    const { table, getFilterComponent, canAddException, tableId } =
        useUserControlProgramListTable(controlPrograms);

    const modeConfig = getFilterModeConfig(t);

    const selectedRows = table.getFilteredSelectedRowModel().rows;
    const deselectAll = () => table.resetRowSelection();

    const {
        removeAccessRightToolState,
        isRemoveAccessRightModalOpen,
        openRemoveAccessRightModal,
        closeRemoveAccessRightModal,
        removeAccessRight,
        isRemovingAccessRight,
    } = useRemoveControlProgramAccessRightUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    const {
        replaceUserToolState,
        isReplaceUserModalOpen,
        openReplaceUserModal,
        closeReplaceUserModal,
        replaceUser,
        isReplacingUser,
    } = useReplaceControlProgramUserUtils({
        userId,
        selectedRows,
        deselectAll,
    });

    return (
        <div>
            <RemoveUserModal
                onClose={closeRemoveAccessRightModal}
                onSubmit={() => void removeAccessRight()}
                open={isRemoveAccessRightModalOpen}
                loading={isRemovingAccessRight}
                message={t(
                    "AccessAndAssignment_RemoveControlProgramDialog_Text",
                    {
                        user: formatUserName(user),
                        count: selectedRows.length,
                    }
                )}
            />
            <ReplaceUserModal
                open={isReplaceUserModalOpen}
                replaceType="WithUser"
                loading={isReplacingUser}
                onClose={closeReplaceUserModal}
                onClick={(userOrGroup) => void replaceUser(userOrGroup)}
                user={user}
            />
            <AssignmentTable<ControlProgramRow>
                table={table}
                tableId={tableId}
                getFilterComponent={getFilterComponent}
                canAddException={canAddException}
                modeConfig={modeConfig}
                toolbar={
                    <TableToolbar>
                        <TextTool
                            state={removeAccessRightToolState}
                            onClick={openRemoveAccessRightModal}
                        >
                            {t(
                                "AccessAndAssignment_Tool_RemoveUserFromControlPrograms"
                            )}
                        </TextTool>
                        <TextTool
                            state={replaceUserToolState}
                            onClick={openReplaceUserModal}
                        >
                            {t("AccessAndAssignment_Tool_ReplaceUser")}
                        </TextTool>
                    </TableToolbar>
                }
            />
        </div>
    );
}
